import React, { useMemo, useState } from "react";
import { FormHelperText } from "@mui/material";
import DatePicker from "@components/DatePicker/DatePicker";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { createUpdatePortServiceTimesSchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/updatePortServiceTimesSchema";
import type { UpdateDrawerInfo } from "@features/vessel-call/vessel-call-status/PortServices/hoc/withDrawers";
import { useUpdatePortService } from "@features/vessel-call/vessel-call-status/PortServices/hooks/useUpdatePortServiceTimes";
import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.port-services";
const STARTED_STATUS = STATUS_KEYS.STARTED;

const asDateOrNull = (v: unknown): Date | null => {
  if (v == null || v === "") {
    return null;
  }

  const parsed = v instanceof Date ? new Date(v.getTime()) : new Date(v as string | number);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

interface UpdateServiceDrawerProps {
  drawerInfo: UpdateDrawerInfo | null;
  onClose: () => void;
  setShouldRefetch: (value: boolean) => void;
}

const UpdateServiceDrawer: React.FC<UpdateServiceDrawerProps> = ({
  drawerInfo,
  onClose,
  setShouldRefetch,
}) => {
  const { t } = useTranslation();
  const isStarted = drawerInfo?.currentStatus === STARTED_STATUS;
  const [ets, setEts] = useState<Date | null>(asDateOrNull(drawerInfo?.ets));
  const [etc, setEtc] = useState<Date | null>(asDateOrNull(drawerInfo?.etc));
  const [errors, setErrors] = useState<{
    ets?: string;
    etc?: string;
    general?: string;
  }>({});
  const existingEts = asDateOrNull(drawerInfo?.ets);

  const minEtcDate = useMemo(() => {
    if (isStarted) {
      return new Date();
    }

    const effectiveEts = ets ?? existingEts;

    if (!effectiveEts) {
      return new Date();
    }

    const etsMin = new Date(effectiveEts);
    etsMin.setMinutes(etsMin.getMinutes() + 1);

    return etsMin;
  }, [ets, existingEts, isStarted]);

  const { onSubmit } = useUpdatePortService({
    status: drawerInfo?.currentStatus || "",
    portServiceId: drawerInfo?.id || "",
    ets,
    etc,
    setShouldRefetch,
    onClose,
  });

  if (!drawerInfo) return null;

  const handleSubmit = () => {
    const hasEts = ets instanceof Date && !Number.isNaN(ets.getTime());
    const hasEtc = etc instanceof Date && !Number.isNaN(etc.getTime());

    if (isStarted && !hasEtc) {
      setErrors({ etc: t("form-validation.mandatory", { label: "ETC" }) });
      return;
    }

    if (!isStarted && !hasEts && !hasEtc) {
      setErrors({
        general: t(`${LANG_BASE}.notifications.service-update-error-no-times-description`),
      });
      return;
    }

    const normalizedEts = isStarted ? existingEts : (ets ?? existingEts);
    const normalizedEtc = etc;

    const result = createUpdatePortServiceTimesSchema({
      skipEtsPastValidation: isStarted,
    }).safeParse({
      ets: normalizedEts,
      etc: normalizedEtc,
    });

    if (!result.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of result.error.issues) {
        const pathKey = issue.path[0] as keyof typeof fieldErrors;
        if (!pathKey) continue;
        fieldErrors[pathKey] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    onSubmit();
  };

  return (
    <div data-testid="update-port-service-form">
      <SideDrawer
        isOpen={!!drawerInfo?.isOpen}
        title={t(`${LANG_BASE}.fields.update-title`)}
        cancelLabel={t("common.close")}
        onClose={onClose}
        submitLabel={t(`${LANG_BASE}.fields.update-button`)}
        onSubmit={handleSubmit}
      >
        <div className="px-6">
          <div className="min-w-sm max-w-sm">
            <div className="flex flex-col gap-2">
              <CustomFormLabel data-testid="update-service-ets-label">ETS</CustomFormLabel>
              <DatePicker
                hasError={!!errors.ets}
                value={asDateOrNull(isStarted ? drawerInfo.ets : ets)}
                onChange={(d) => {
                  setEts(d);
                  setErrors((prev) => ({ ...prev, ets: undefined }));
                }}
                testId="update-service-ets-datepicker"
                timePicker={true}
                combinedInput={true}
                minDate={new Date()}
                disabled={isStarted}
              />
              {errors.ets && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.ets}</FormHelperText>
              )}
            </div>

            <div className="flex flex-col gap-2 mt-4">
              <CustomFormLabel data-testid="update-service-etc-label">ETC</CustomFormLabel>
              <DatePicker
                hasError={!!errors.etc}
                value={asDateOrNull(etc)}
                onChange={(d) => {
                  setEtc(d);
                  setErrors((prev) => ({ ...prev, etc: undefined }));
                }}
                testId="update-service-etc-datepicker"
                timePicker={true}
                combinedInput={true}
                minDate={minEtcDate}
              />
              {errors.etc && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.etc}</FormHelperText>
              )}
              {errors.general && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.general}</FormHelperText>
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default UpdateServiceDrawer;
